import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserProfileList } from "container/admin";
import { Button } from '@material-ui/core';
import Grid from '@mui/material/Grid2';
import { userProfileAction, alertActions, balancesheetActions, } from '_store';
import UserProfileDetailsMCAdmin from './profiles/UserProfileDetailsMCAdmin';
import { useParams } from 'react-router-dom';
import { saveFiltersToLocalStore, loadFiltersFromLocalStore } from '_utils/searchFilterLocalStorage';
import { toIdArray } from '_utils';
import { ERR_MSG } from 'constants/labels';

// MarketerID/MappingID come back from GetUserProfileBySearch as comma-separated
// strings (e.g. "136,114,101") for portal 4 users; normalize them to number[]
// so the rest of the UI (and the AssignRoleToUser payload) can treat them as arrays.
const normalizeMarketerFields = (users) =>
  (users || []).map((user) => ({
    ...user,
    MarketerID: toIdArray(user.MarketerID),
    MappingID: toIdArray(user.MappingID),
  }));

const Users = () => {
  const header = " User Profile";
  const dispatch = useDispatch();
  const { portal } = useParams();
  const portalId = Number(portal);
  const [data, setData] = useState([]);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailSection, setshowDetailSection] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [editedRowId, setEditedRowId] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [folderPaths, setFolderPaths] = useState([])
  const hasErrors = Object.values(errors).some((msg) => msg && msg.length > 0);
  const authUser = useSelector(x => x.auth?.value);
  const userProfiles = useSelector(x => x.userProfile?.userProfileData);
  const user = authUser?.Data;
  const userdetails = user?.UserDetails;
  const userAccess = user?.UserAccess;
  const authUserName = `${userdetails.firstName} ${userdetails.lastName}`;
  const isReviewer = userAccess?.some(access => access.Role.toLowerCase().includes('reviewer'));
  const isAdmin = userAccess?.some(access => access.Role.toLowerCase().includes('admin'));
  const adminList = userAccess?.filter(access =>
    ["admin", "reviewer","owner","digital-admin"].includes(access.Role.toLowerCase())
  );
  const [userMgmtListFilterParams, setUserMgmtListFilterParams] = useState(() =>
    loadFiltersFromLocalStore("userManagementList"),
  );
  const portalData = adminList ? adminList?.map(admin => ({
    PortalId: admin.PortalId,
    PortalName: admin.PortalName,
    PortalKey: admin.PortalKey,
  })) : [];

  if (isReviewer)
    sessionStorage.setItem('portalID', 3);
  else
    sessionStorage.setItem('portalID', 98);

  useEffect(() => {
    saveFiltersToLocalStore("userManagementList", userMgmtListFilterParams);
  }, [userMgmtListFilterParams]);

  useEffect(() => {
    const fetchData = async () => {
      dispatch(alertActions.clear());
      try {
        const result = await dispatch(userProfileAction.getUserProfile(portalId)).unwrap();
        const userProfileDetails = result?.Data;

        if (userProfileDetails?.CustomerFolders?.length) {
          const pathNames = userProfileDetails.CustomerFolders.map((data) => ({
            label: data.PathName,
            value: data.Id
          }))
          setFolderPaths(pathNames)
        }
        setData({ ...userProfileDetails, User: normalizeMarketerFields(userProfileDetails?.User) });
        handleRefresh();
      } catch (error) {
        dispatch(alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: `${header} Failed`
        }));
      }
    };
    fetchData();
  }, [dispatch, portalId]);


  // Toggle lock/unlock for a specific userId
  const handleLockToggle = async (row) => {
    dispatch(alertActions.clear());
    try {
      const transformedData = {
        UserId: row.UserID,
        CreatedBy: authUserName,
      };

      if (transformedData) {
        await dispatch(userProfileAction.lockProfile(transformedData)).unwrap();
        handleRefresh();
        dispatch(
          alertActions.success({
            message: "Account unlocked successfully.",
            header,
            showAfterRedirect: true,
          }),
        );
      }
    } catch (error) {
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    }
  };

  const handleReject = async (user, reason, comments) => {
    dispatch(alertActions.clear());
    try {
      const transformedData = {
        UserID: user?.UserID,
        RejectionReasonID: reason?.[0]?.value,
        RejectionReason: reason?.[0]?.label,
        RejectionComment: comments,
        UpdatedBy: authUserName,
      };

      if (transformedData) {
        await dispatch(userProfileAction.rejectUser(transformedData)).unwrap();
        handleRefresh();
        dispatch(
          alertActions.success({
            message: "Registration request rejected.",
            header,
            showAfterRedirect: true,
          }),
        );
      }
    } catch (error) {
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    }
  };

  const hasEaChanged = (newData) => {
    const referenceData = selectedRows.find(
      (item) => item.UserID === newData.UserID
    );
    const whitelist = ["LastName", "FirstName", "FullName"];
    const hasNulls = Object.keys(newData)
      .filter((key) => !whitelist.includes(key))
      .some((key) => newData[key] === null || newData[key] === "");

    if (hasNulls) {
      const { Status, RoleID } = newData;
      if (Status === "Approved" && RoleID === "") {
        setIsDataChanged(true);
      } else {
        setIsDataChanged(false);
      }
      return;
    }
    const result = Object.keys(referenceData).some(
      (key) => referenceData[key] !== newData[key]
    );
    setIsDataChanged(result && !hasNulls);
  };

  const handleChange = (newValue, rowData, field) => {
    setEditedRowId((prev) => {
      const updatedRows = { ...prev };
      if (!updatedRows[rowData.UserID]) {
        updatedRows[rowData.UserID] = { ...rowData };
      }
      updatedRows[rowData.UserID][field] = newValue;
      return updatedRows;
    });
    const newData = data?.User?.map(row => row.UserID === rowData.UserID ? { ...row, [field]: newValue, isEdited: true } : row);
    setData(pre => ({ ...pre, User: [...newData] }));
    const updatedData = newData.find((item) => item.UserID == rowData.UserID)

    if (portalId === 2) {
      hasEaChanged(updatedData);
    } else {
      if (
        updatedData.RoleID ===10 &&
        (
          !updatedData?.FolderPathID ||
          !updatedData.MarketerID?.length ||
          !updatedData.CompanyName
        )
      ) {
        setIsDataChanged(false);
      } else {
        setIsDataChanged(true);
      }
    }
  };

  const singleUserUpdate = (rowData) => {
    handleSubmit(true, rowData);
  }

  const handleDelete = async (rowsToDelete) => {
    dispatch(alertActions.clear());
    try {
      const userIdsToDelete = Array.isArray(rowsToDelete)
        ? rowsToDelete.map((row) => row.UserID) // For bulk delete
        : [rowsToDelete.original.UserID]; // For single row delete
      // // Filter out the individual row to be delete

      setIsModalOpen(false);

      // Get the deleted profiles for transformation
      const deletedProfiles = userProfiles?.User?.filter((item) =>
        userIdsToDelete.includes(item.UserID),
      );

      // Transform the deleted profiles for further processing
      const transformedData = deletedProfiles.map((item) => ({
        UserID: item.UserID,
        UpdatedBy: authUserName,
      }));

      if (transformedData.length > 0) {
        const result = await dispatch(
          userProfileAction.delete(transformedData),
        ).unwrap();
        dispatch(
          alertActions.success({
            message: result?.Message,
            header,
            showAfterRedirect: true,
          }),
        );
        handleRefresh();
      }
    } catch (error) {
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    }
  };

  const handleSubmit = async (isSingle = false, singleRowData = null) => {
    dispatch(alertActions.clear());
    try {
      let editedRowData;
      let selectedUsers;

      if (isSingle && singleRowData) {
        // Handle a single object update
        editedRowData = [singleRowData]; // Wrap in an array for consistency
        selectedUsers = editedRowData;
      } else {
        // Handle multiple object updates
        editedRowData = Object.values(editedRowId);

        selectedUsers = editedRowData
          .filter(newItem =>
            selectedRows.some(oldItem => oldItem.UserID === newItem.UserID)
          ).map(item => item);
      }

      const transformedData = (Array.isArray(selectedUsers) ? selectedUsers : []).map((item) => ({
        MappingID: toIdArray(item.MappingID),
        UserID: item.UserID,
        CompanyName: item.CompanyName,
        PortalID: portalId,
        RoleID: item.RoleID || 0,
        AgencyID: item.AgencyID || '',
        JurisdictionID: item.JurisdictionID || '',
        folderPathId:item.RoleID==10? item.FolderPathID : 0,
        MarketerID: toIdArray(item.MarketerID),
        UpdatedBy: authUserName
      }))

      if (transformedData.length > 0) {
        const result = await dispatch(
          userProfileAction.update(transformedData),
        ).unwrap();
        dispatch(
          alertActions.success({
            message: result?.Message,
            header,
            showAfterRedirect: true,
          }),
        );
      }
      handleRefresh();
    } catch (error) {
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    }
  };

  const handleFilterSubmit = async (newData) => {
    if(newData !==null)
      setData(newData);
    else 
  setData((prevData) => ({
    ...prevData,
    User: [],
  }));
  };

  const handleRefresh = async (isClearFilter = false) => {
    try {
      let data;

      if (
        userMgmtListFilterParams &&
        userMgmtListFilterParams?.PortalId !== portalId
      ) {
        data = { PortalId: portalId };
        setUserMgmtListFilterParams(null);
      } else if (!userMgmtListFilterParams || isClearFilter) {
        data = { PortalId: portalId };
      } else {
        data = userMgmtListFilterParams;
      }
      const newData = await dispatch(userProfileAction.filter(data)).unwrap();
      if (newData?.Data) {
        const userProfileDetails = newData?.Data;
        if (userProfileDetails?.CustomerFolders?.length) {
          const pathNames = userProfileDetails.CustomerFolders.map((data) => ({
            label: data.PathName,
            value: data.Id
          }))
          setFolderPaths(pathNames)
        }
        setData({ ...userProfileDetails, User: normalizeMarketerFields(userProfileDetails?.User) });
      }
      setIsDataChanged(false);
      setSelectedUser(null);
      setshowDetailSection(false);
      setSelectedRows([]);
      setRowSelection({});
      setEditedRowId({});
      isClearFilter && setUserMgmtListFilterParams(null);
    } catch (error) {
      dispatch(alertActions.clear());
      handleFilterSubmit(null);
      if (error.message) {
        dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
      }
    }
  }

  const handleCancelClick = async () => {
    await handleRefresh();
  };
  const checkFolderPath = async (name) => {
    dispatch(alertActions.clear());
    try {
      const result = await dispatch(balancesheetActions.getByPathName(name)).unwrap();
      if (result.Data?.ID) {
        dispatch(alertActions.error({ message: "Folder path already exists.", header }));
        return false
      } else return true
    }
    catch (error) {
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
      return false
    }
  }
  const createPath = async (name, rowData, folderPath) => {
    dispatch(alertActions.clear());
    try {
      const isValid = await checkFolderPath(folderPath)
      if (!isValid) return
      const payload = {
        "createFolderAndUploadFiles": {
          "Id": 0,
          "PathName": name,
          "SupplierArea": rowData.UserID.toString(),
          "customerFolderName": folderPath,
          "MappingType": "C"

        },
        "overwrite": true
      }
      await dispatch(balancesheetActions.createFolder(payload)).unwrap();
      const pathName = await dispatch(balancesheetActions.getByPathName(folderPath)).unwrap();
      if (pathName.Data?.ID) {
        setFolderPaths((prev) => [
          ...prev,
          {
            value: pathName.Data.ID,
            label: pathName.Data.PathName,
          }
        ]);
        handleChange(pathName.Data?.ID, rowData, "FolderPathID")
      }
    }
    catch (error) {
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    }
  }

  return (
    <>
      {/* {!(userProfiles?.loading || userProfiles?.error) && */}
      <>
        {!showDetailSection && (<>
          <UserProfileList
            portalData={portalData}
            userProfiles={data}
            portalId={portalId}
            header={header}
            // setPortalId={setPortalId}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            handleDelete={handleDelete}
            handleReject={handleReject}
            onLockToggle={handleLockToggle}
            handleChange={handleChange}
            singleUserUpdate={singleUserUpdate}
            handleFilterSubmit={handleFilterSubmit}
            setSelectedUser={setSelectedUser}
            setshowDetailSection={setshowDetailSection}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            handleRefresh={handleRefresh}
            isAdmin={isAdmin}
            isReviewer={isReviewer}
            errors={errors}
            setErrors={setErrors}
            createPath={createPath}
            folderPaths={folderPaths}
            userMgmtListFilterParams={userMgmtListFilterParams}
            setUserMgmtListFilterParams={setUserMgmtListFilterParams}
          />
          <Grid size={{ xs: 12, sm: 12, md: 12 }} className="Personal-Information">
            <Button variant="contained" color="red" className="cancelbutton" onClick={handleCancelClick}>
              Cancel
            </Button>
            <Button type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className='submitbutton'
              onClick={() => handleSubmit()}
              // disabled={!isDataChanged} >
              disabled={selectedRows.length === 0 || !isDataChanged || hasErrors}>
              Save
            </Button>
          </Grid>
        </>)}
        {showDetailSection &&
          <UserProfileDetailsMCAdmin selectedUser={selectedUser} setSelectedUser={setSelectedUser} setshowDetailSection={setshowDetailSection} handleReject={handleReject} handleRefresh={handleRefresh} />
        }
      </>
      {/* } */}
    </>
  );
};

export default Users;